// src/demo/seed.js
//
// Demo Mode · single canonical seed fixture. Per build prompt §4.1:
// "Define the canonical demo dataset once, in code/config (not a
// live prod table). This is the only place demo content is
// authored, so the demo can't drift screen-to-screen."
//
// COMPLIANCE INVARIANTS (load-bearing — §4.2 + §4.3):
//
//   1. EVERY phone number is in the 555-01xx test prefix (FCC-reserved
//      for fiction/testing). Email domains are @example.com /
//      @example.org / @example.net (RFC-2606 reserved). Addresses
//      use placeholder street numbers. No real PII ever reaches the
//      seed.
//
//   2. Medicare content carries the CMS TPMO disclaimer + SOA-before-
//      benefits sequence. No "free quote" language. Plan-specific
//      benefits never named before the SOA is shown signed in the
//      thread.
//
//   3. ACA "$0 plan" / subsidy language is always conditioned with
//      "may qualify" / "depends on income."
//
//   4. Life / IUL figures are labeled "illustrative, not guaranteed."
//
//   5. The purchased-list demo lead shows opt-in captured BEFORE the
//      SMS path was unlocked (email → reply → consent → SMS), per
//      §4.3 cold-outreach rule.
//
//   6. AI Employee names match the production codebase:
//      NOVA / ENROLL / RETAIN / ANCHOR / LEDGER / GUIDE /
//      AUDIT / CONSENT / SHIELD / ASCEND. Per build prompt §4.3 #5
//      + the operator's recon confirmation. Friendly aliases are
//      documented in AI_EMPLOYEES below but the production name is
//      the canonical label.

'use strict';

// ── 1. Leads (12 — mix of source / vertical / stage) ───────────
const LEADS = [
  {
    id: 'demo-lead-001',
    name: 'John Smith', firstName: 'John', lastName: 'Smith',
    phone: '+15550111234', email: 'john.smith@example.com',
    address: '100 Demo Lane', city: 'Austin', state: 'TX', zip: '78701',
    birthday: '1967-04-12',
    age: 58,
    source: 'Facebook Ad — IUL Wealth Calc', source_kind: 'paid_ad',
    leadType: 'iul', vertical: 'life',
    stage: 'appointment_set',
    assigned_ai: 'NOVA',
    tags: ['hot', 'replied', 'iul_curious', 'budget_500'],
    notes: 'Replied to 3 NOVA texts. Opened IUL illustration link. Wife also interested. Prefers texts after 6pm.',
    createdAt: '2026-06-15T14:23:00Z',
    score: 5,
    next_action: 'Booked: Thursday 2pm Zoom (Calendar entry demo-cal-002)',
  },
  {
    id: 'demo-lead-002',
    name: 'Sarah Johnson', firstName: 'Sarah', lastName: 'Johnson',
    phone: '+15550111235', email: 'sarah.johnson@example.org',
    address: '200 Sample Ave', city: 'Phoenix', state: 'AZ', zip: '85001',
    birthday: '1989-11-03',
    age: 36,
    source: 'HealthCare.gov referral', source_kind: 'inbound',
    leadType: 'aca_marketplace', vertical: 'health',
    stage: 'quoted_followup',
    assigned_ai: 'NOVA',
    tags: ['hot', 'subsidy_eligible_likely', 'family_of_4'],
    notes: 'Lost employer coverage. Family of 4. Income ~$75k — may qualify for substantial APTC depending on household details. SEP qualifying event (loss of MEC) documented.',
    createdAt: '2026-06-22T09:11:00Z',
    score: 5,
    next_action: 'Awaiting CMS Marketplace consent-to-assist e-sign',
  },
  {
    id: 'demo-lead-003',
    name: 'Maria Lopez', firstName: 'Maria', lastName: 'Lopez',
    phone: '+15550111236', email: 'maria.lopez@example.com',
    address: '300 Test Pl', city: 'Miami', state: 'FL', zip: '33101',
    birthday: '1961-07-26',
    age: 64,
    source: 'T65 cohort — turning 65 in 30 days', source_kind: 'cohort',
    leadType: 'medicare', vertical: 'medicare',
    stage: 'soa_pending',
    assigned_ai: 'ENROLL',
    tags: ['t65', 'soa_required_before_plan_talk', 'aep_window'],
    notes: 'Turning 65 on Jul 26. ENROLL sent SOA request via SMS + email (Phase 2 of ENROLL — both channels). 48-hour wait clock starts on signature. No plan benefits discussed yet per CMS TPMO rule.',
    createdAt: '2026-06-20T16:45:00Z',
    score: 4,
    next_action: 'Awaiting SOA signature; then 48h wait before benefits discussion',
  },
  {
    id: 'demo-lead-004',
    name: 'Robert Chen', firstName: 'Robert', lastName: 'Chen',
    phone: '+15550111237', email: 'robert.chen@example.org',
    address: '400 Fixture Dr', city: 'Denver', state: 'CO', zip: '80201',
    birthday: '1978-02-14',
    age: 48,
    source: 'ANCHOR — HealthSherpa issue intake', source_kind: 'issue',
    leadType: 'aca_marketplace', vertical: 'health',
    stage: 'client_active',
    assigned_ai: 'ANCHOR',
    tags: ['active_client', 'income_verification_pending', 'deadline_14d'],
    notes: 'Active Aetna ACA client. CMS income verification request from Marketplace — deadline 14 days. ANCHOR sent Day-0 SMS. Awaiting pay stub upload.',
    createdAt: '2025-11-04T10:00:00Z',
    score: 5,
    next_action: 'ANCHOR Day-1 follow-up fires tomorrow if no reply',
  },
  {
    id: 'demo-lead-005',
    name: 'David Cherryholmes', firstName: 'David', lastName: 'Cherryholmes',
    phone: '+15550111238', email: 'david.c@example.com',
    address: '500 Mock Blvd', city: 'Krum', state: 'TX', zip: '76249',
    birthday: '1962-08-22',
    age: 63,
    source: 'Cross-sell from FEX client', source_kind: 'cross_sell',
    leadType: 'final_expense', vertical: 'life',
    stage: 'client_active',
    assigned_ai: 'RETAIN',
    tags: ['active_client', 'nsf_recovery', 'fex'],
    notes: 'Transamerica FEX $250/mo. Recent NSF — RETAIN cadence in flight. Grace window: 30 days. Workflow Engine NSF auto-call queued for tomorrow.',
    createdAt: '2026-03-10T11:30:00Z',
    score: 5,
    next_action: 'Auto-call queued (canDial gated — gated by demo seal)',
  },
  {
    id: 'demo-lead-006',
    name: 'Patricia Williams', firstName: 'Patricia', lastName: 'Williams',
    phone: '+15550111239', email: 'pwilliams@example.org',
    address: '600 Demo Ct', city: 'Chicago', state: 'IL', zip: '60601',
    birthday: '1955-12-05',
    age: 70,
    source: 'Birthday Rule cohort (IL 45-day window)', source_kind: 'cohort',
    leadType: 'medicare_supplement', vertical: 'medicare',
    stage: 'in_window',
    assigned_ai: 'ENROLL',
    tags: ['birthday_rule', 'il_45_day', 'medigap_swap_eligible'],
    notes: 'IL Medigap Birthday Rule window OPEN — 18 days remaining. Currently Plan G with Mutual of Omaha. ENROLL flagged for re-shop conversation. SOA on file (signed 2025-11-12).',
    createdAt: '2026-06-08T09:00:00Z',
    score: 4,
    next_action: 'Submit Plan G application with new carrier by day 30 of window',
  },
  {
    id: 'demo-lead-007',
    name: 'Tom Wilson', firstName: 'Tom', lastName: 'Wilson',
    phone: null, email: 'tom.wilson@example.net',
    address: '700 Faux Way', city: 'Atlanta', state: 'GA', zip: '30301',
    birthday: '1983-09-15',
    age: 42,
    source: 'Purchased list — Q2 batch', source_kind: 'purchased_list',
    leadType: 'term_life', vertical: 'life',
    stage: 'opt_in_pending',
    assigned_ai: 'NOVA',
    tags: ['purchased_list', 'email_only_until_opt_in', 'cold'],
    notes: 'COLD-OUTREACH RULE: SMS unlocked ONLY after explicit opt-in. NOVA sent CAN-SPAM-compliant cold email; awaiting reply. Phone field intentionally null until opt-in captured.',
    createdAt: '2026-06-24T08:00:00Z',
    score: 1,
    next_action: 'Wait for email reply → consent capture → THEN SMS path unlocks',
  },
  {
    id: 'demo-lead-008',
    name: 'Linda Martinez', firstName: 'Linda', lastName: 'Martinez',
    phone: '+15550111240', email: 'linda.m@example.com',
    address: '800 Test Rd', city: 'San Antonio', state: 'TX', zip: '78201',
    birthday: '1970-05-30',
    age: 56,
    source: 'Referral — referred by John Smith (lead-001)', source_kind: 'referral',
    leadType: 'iul', vertical: 'life',
    stage: 'new',
    assigned_ai: 'NOVA',
    tags: ['referral', 'warm', 'iul_curious'],
    notes: 'Referred by John Smith. NOVA fires welcome cadence today.',
    createdAt: '2026-06-25T17:30:00Z',
    score: 3,
    next_action: 'NOVA Day-0 welcome SMS queued',
  },
  {
    id: 'demo-lead-009',
    name: 'Greg Anderson', firstName: 'Greg', lastName: 'Anderson',
    phone: '+15550111241', email: 'greg@example.org',
    address: '900 Demo St', city: 'Portland', state: 'OR', zip: '97201',
    birthday: '1995-01-08',
    age: 31,
    source: 'Google search "best ACA Oregon"', source_kind: 'organic',
    leadType: 'aca_marketplace', vertical: 'health',
    stage: 'no_show',
    assigned_ai: 'NOVA',
    tags: ['no_show_recovery'],
    notes: 'Booked appt last week, no-showed. NOVA recovery cadence in flight.',
    createdAt: '2026-06-12T13:15:00Z',
    score: 2,
    next_action: 'NOVA Day-3 recovery SMS fires tomorrow',
  },
  {
    id: 'demo-lead-010',
    name: 'Frank DeLuca', firstName: 'Frank', lastName: 'DeLuca',
    phone: '+15550111242', email: 'fdeluca@example.com',
    address: '1000 Sample St', city: 'Newark', state: 'NJ', zip: '07101',
    birthday: '1948-03-19',
    age: 78,
    source: 'AEP campaign — renewal at risk', source_kind: 'campaign',
    leadType: 'medicare_advantage', vertical: 'medicare',
    stage: 'aep_review_due',
    assigned_ai: 'ENROLL',
    tags: ['aep', 'anoc_review', 'plan_change_2027'],
    notes: '2027 ANOC arrived from Humana — benefit changes detected. ENROLL sent SOA refresh (signed 2026-06-18). AEP review call booked.',
    createdAt: '2024-10-15T00:00:00Z',
    score: 4,
    next_action: 'AEP review Zoom — Friday 11am',
  },
  {
    id: 'demo-lead-011',
    name: 'Helen Park', firstName: 'Helen', lastName: 'Park',
    phone: '+15550111243', email: 'hpark@example.org',
    address: '1100 Mock Ln', city: 'Seattle', state: 'WA', zip: '98101',
    birthday: '1958-10-11',
    age: 67,
    source: 'Cross-sell — added dental to existing Medigap', source_kind: 'cross_sell',
    leadType: 'dental', vertical: 'dental_vision',
    stage: 'client_active',
    assigned_ai: 'RETAIN',
    tags: ['active_client', 'dental', 'preventive_window'],
    notes: 'Delta Dental added 2026-04. Preventive visit reminder due in 30 days. Workflow Engine queued.',
    createdAt: '2026-04-02T00:00:00Z',
    score: 3,
    next_action: 'Preventive visit SMS reminder day 30',
  },
  {
    id: 'demo-lead-012',
    name: 'Eric Brown', firstName: 'Eric', lastName: 'Brown',
    phone: '+15550111244', email: 'eric.brown@example.com',
    address: '1200 Test Ave', city: 'Charlotte', state: 'NC', zip: '28201',
    birthday: '1972-06-04',
    age: 53,
    source: 'Final expense — funeral home leads', source_kind: 'paid_ad',
    leadType: 'final_expense', vertical: 'life',
    stage: 'underwriting',
    assigned_ai: 'NOVA',
    tags: ['fex', 'simplified_issue', 'application_pending'],
    notes: 'FEX simplified-issue app submitted to Mutual of Omaha. Awaiting carrier underwriting. NOVA handoff to ENROLL on issue.',
    createdAt: '2026-06-18T15:00:00Z',
    score: 3,
    next_action: 'Awaiting carrier UW decision (typical 3-5 business days)',
  },
  // ── Additional health-vertical leads so the Health Pipeline
  // ── isolated view feels populated in demo mode. Mix of ACA
  // ── marketplace + Medicare Advantage + Medigap + SEP triggers.
  {
    id: 'demo-lead-013',
    name: 'Diana Foster', firstName: 'Diana', lastName: 'Foster',
    phone: '+15550111247', email: 'd.foster@example.com',
    address: '1201 Oak Ridge Dr', city: 'Nashville', state: 'TN', zip: '37201',
    birthday: '1960-11-20',
    age: 65,
    source: 'Facebook Ad — Medicare 101', source_kind: 'paid_ad',
    leadType: 'medicare_advantage', vertical: 'health',
    stage: 'quoted_followup',
    assigned_ai: 'NOVA',
    tags: ['hot', 'aging_in', 'IEP_active'],
    notes: 'Just turned 65 in November — IEP window open. Currently on employer COBRA, needs Medicare Advantage. Has diabetes + hypertension — need plan with good pharmacy coverage.',
    createdAt: '2026-06-25T10:15:00Z',
    score: 5,
    next_action: 'Sending SOA + AoR — Zoom booked Wednesday 3pm',
  },
  {
    id: 'demo-lead-014',
    name: 'Frank Rodriguez', firstName: 'Frank', lastName: 'Rodriguez',
    phone: '+15550111248', email: 'frank.r@example.org',
    address: '450 Sunset Blvd', city: 'Tampa', state: 'FL', zip: '33602',
    birthday: '1985-06-14',
    age: 40,
    source: 'HealthCare.gov marketplace', source_kind: 'inbound',
    leadType: 'aca_marketplace', vertical: 'health',
    stage: 'appointment_set',
    assigned_ai: 'NOVA',
    tags: ['SEP_qualifying', 'family_of_3', 'subsidy_likely'],
    notes: 'Recently moved to FL from NY — permanent move triggers SEP. Household income ~$62k, family of 3 → APTC eligible. Kids need pediatric dental.',
    createdAt: '2026-06-28T11:30:00Z',
    score: 5,
    next_action: 'Enrollment Zoom Thursday 2pm — bringing tax return + move docs',
  },
  {
    id: 'demo-lead-015',
    name: 'Barbara Wells', firstName: 'Barbara', lastName: 'Wells',
    phone: '+15550111249', email: 'b.wells@example.com',
    address: '78 Maple Grove Way', city: 'Charlotte', state: 'NC', zip: '28201',
    birthday: '1957-09-30',
    age: 68,
    source: 'Referral from Dr. Sarah Miller', source_kind: 'referral',
    leadType: 'medicare_supplement', vertical: 'health',
    stage: 'quoted_followup',
    assigned_ai: 'NOVA',
    tags: ['warm_referral', 'high_ltv', 'medigap_shopper'],
    notes: 'Referred by Dr. Miller (referral partner). Currently on Aetna MA, wants to switch to Medigap Plan G with UHC. Health is excellent, no chronic conditions.',
    createdAt: '2026-06-30T09:45:00Z',
    score: 5,
    next_action: 'Illustration + underwriting app queued — call back Thursday 10am',
  },
  {
    id: 'demo-lead-016',
    name: 'Carlos Mendez', firstName: 'Carlos', lastName: 'Mendez',
    phone: '+15550111250', email: 'c.mendez@example.net',
    address: '2200 Camelback Rd', city: 'Phoenix', state: 'AZ', zip: '85015',
    birthday: '1972-04-08',
    age: 53,
    source: 'Google Ads — ACA subsidy calc', source_kind: 'paid_ad',
    leadType: 'aca_marketplace', vertical: 'health',
    stage: 'opt_in_pending',
    assigned_ai: 'NOVA',
    tags: ['cold_needs_warmup', 'self_employed'],
    notes: 'Self-employed contractor. Currently uninsured. Income variable — needs subsidy estimate. Hasn\'t replied to first 2 NOVA texts. Try email next.',
    createdAt: '2026-06-23T14:22:00Z',
    score: 3,
    next_action: 'Sending subsidy calc email + video walkthrough',
  },
  {
    id: 'demo-lead-017',
    name: 'Helen Kim', firstName: 'Helen', lastName: 'Kim',
    phone: '+15550111251', email: 'helen.kim@example.com',
    address: '340 Peachtree St NE', city: 'Atlanta', state: 'GA', zip: '30303',
    birthday: '1962-02-11',
    age: 63,
    source: 'SEP trigger — turning 65 in 24 months', source_kind: 'internal',
    leadType: 'medicare_planning', vertical: 'health',
    stage: 'no_show',
    assigned_ai: 'NOVA',
    tags: ['T63', 'aging_in_prep', 'cross_sell_target'],
    notes: 'Existing ACA client aging into Medicare in 24 months. Started T63 education sequence. Missed the first planning call — reschedule.',
    createdAt: '2026-06-20T16:00:00Z',
    score: 4,
    next_action: 'Reschedule planning call — offer Tues/Thurs 6pm slots',
  },
  {
    id: 'demo-lead-018',
    name: 'Trevor Nash', firstName: 'Trevor', lastName: 'Nash',
    phone: '+15550111252', email: 't.nash@example.org',
    address: '155 Beach Dr', city: 'Charleston', state: 'SC', zip: '29401',
    birthday: '1979-10-25',
    age: 46,
    source: 'HealthSherpa broker link', source_kind: 'inbound',
    leadType: 'aca_marketplace', vertical: 'health',
    stage: 'quoted_followup',
    assigned_ai: 'NOVA',
    tags: ['warm', 'high_earner', 'silver_shopping'],
    notes: 'Household income $145k — no subsidy but wants coverage. Comparing silver plans across BCBS/Ambetter/Oscar. Health-conscious — wants HSA-eligible plan.',
    createdAt: '2026-06-27T13:10:00Z',
    score: 4,
    next_action: 'Sending HSA-eligible bronze/silver comparison PDF',
  },
  {
    id: 'demo-lead-019',
    name: 'Grace Okafor', firstName: 'Grace', lastName: 'Okafor',
    phone: '+15550111253', email: 'g.okafor@example.com',
    address: '892 Elm Ave', city: 'Houston', state: 'TX', zip: '77002',
    birthday: '1955-12-05',
    age: 70,
    source: 'AEP campaign response', source_kind: 'inbound',
    leadType: 'medicare_advantage', vertical: 'health',
    stage: 'appointment_set',
    assigned_ai: 'ENROLL',
    tags: ['AEP_reshop', 'plan_change', 'network_issue'],
    notes: 'AEP re-shop. Current MA plan dropped her PCP from network. Needs to change to plan that includes MD Anderson network. Widow, sensitive to premium changes.',
    createdAt: '2026-07-01T09:00:00Z',
    score: 5,
    next_action: 'SOA signed Zoom booked Thursday 11am — bring 3 MA options',
  },
];

// ── 2. Inbox threads (6 — each shows an AI agent doing real work) ──
const INBOX_THREADS = [
  {
    id: 'demo-thread-001',
    lead_id: 'demo-lead-001',
    lead_name: 'John Smith',
    lead_phone: '+15550111234',
    assigned_ai: 'NOVA',
    last_message_at: '2026-06-25T18:22:00Z',
    unread: false,
    messages: [
      { id: 'm1', from: 'agent', via: 'NOVA', at: '2026-06-23T14:00:00Z',
        body: 'Hi John — thanks for checking out the IUL calculator. Quick question: are you mostly thinking about retirement income, cash you can borrow against, or both?' },
      { id: 'm2', from: 'lead', at: '2026-06-23T18:42:00Z',
        body: 'Both honestly. Want to make sure my wife is covered too.' },
      { id: 'm3', from: 'agent', via: 'NOVA', at: '2026-06-23T18:50:00Z',
        body: 'That tracks with most of my clients in their late 50s. Quick illustration takes 10 minutes on a Zoom — I can walk you through the numbers for a couple scenarios. (Illustration only; not a guarantee of future performance.) Thursday 2pm or Friday 10am work?' },
      { id: 'm4', from: 'lead', at: '2026-06-25T18:15:00Z',
        body: 'Thursday 2pm works' },
      { id: 'm5', from: 'agent', via: 'NOVA', at: '2026-06-25T18:22:00Z',
        body: 'Booked! Calendar invite + Zoom link in your inbox. Talk Thursday.' },
    ],
  },
  {
    id: 'demo-thread-002',
    lead_id: 'demo-lead-003',
    lead_name: 'Maria Lopez',
    lead_phone: '+15550111236',
    assigned_ai: 'ENROLL',
    last_message_at: '2026-06-25T11:00:00Z',
    unread: true,
    messages: [
      { id: 'm1', from: 'agent', via: 'ENROLL', at: '2026-06-25T10:55:00Z',
        body: 'Hi Maria — congrats on turning 65! Before we can discuss any specific Medicare plans, CMS requires a quick form called a Scope of Appointment. Takes 60 seconds to e-sign. I\'ll send it to your email + a link here.\n\nWe do not offer every plan available in your area. Any information we provide is limited to those plans we do offer in your area. Please contact Medicare.gov or 1-800-MEDICARE to get information on all of your options.' },
      { id: 'm2', from: 'agent', via: 'ENROLL', at: '2026-06-25T11:00:00Z',
        body: 'SOA link: https://example.com/soa/demo (signed copy returns to your file). After we have it on file there\'s a 48-hour wait per CMS rules before we can review plan benefits — so we\'ll talk specifics on Friday.' },
    ],
  },
  {
    id: 'demo-thread-003',
    lead_id: 'demo-lead-004',
    lead_name: 'Robert Chen',
    lead_phone: '+15550111237',
    assigned_ai: 'ANCHOR',
    last_message_at: '2026-06-25T09:08:00Z',
    unread: false,
    messages: [
      { id: 'm1', from: 'agent', via: 'ANCHOR', at: '2026-06-25T09:00:00Z',
        body: 'Hi Robert, this is your agent — your Aetna plan has an income verification request from the Marketplace. Deadline is 14 days. Reply YES and I\'ll walk you through exactly what they need.' },
      { id: 'm2', from: 'lead', at: '2026-06-25T09:08:00Z',
        body: 'YES — what do I need?' },
      { id: 'm3', from: 'agent', via: 'ANCHOR', at: '2026-06-25T09:09:00Z',
        body: 'Easiest path: text me a photo of your most recent pay stub. I\'ll upload it through HealthSherpa and confirm it landed. If you\'re self-employed instead, a tax return excerpt works too.' },
    ],
  },
  {
    id: 'demo-thread-004',
    lead_id: 'demo-lead-005',
    lead_name: 'David Cherryholmes',
    lead_phone: '+15550111238',
    assigned_ai: 'RETAIN',
    last_message_at: '2026-06-25T08:30:00Z',
    unread: true,
    messages: [
      { id: 'm1', from: 'agent', via: 'RETAIN', at: '2026-06-25T08:30:00Z',
        body: 'Hey David — your Transamerica draft came back unpaid. Happens all the time — usually a new card or closed account. 30-day grace window. Fastest fix is to log into the Transamerica portal. Want me to text you the link?' },
    ],
  },
  {
    id: 'demo-thread-005',
    lead_id: 'demo-lead-007',
    lead_name: 'Tom Wilson',
    lead_phone: null,
    assigned_ai: 'NOVA',
    last_message_at: '2026-06-24T08:05:00Z',
    unread: false,
    channel_note: 'EMAIL ONLY — SMS path locked until opt-in (purchased list compliance).',
    messages: [
      { id: 'm1', from: 'agent', via: 'NOVA', channel: 'email', at: '2026-06-24T08:05:00Z',
        body: 'Subject: Term life coverage — quick question\n\nHi Tom — I received your information about looking into term life insurance. I\'m a licensed agent in GA. If you want me to send over a no-obligation quote, just reply with your DOB and the coverage amount you\'re thinking about. If you\'d rather I don\'t reach out, reply STOP and I won\'t bother you again.' },
    ],
  },
  {
    id: 'demo-thread-006',
    lead_id: 'demo-lead-010',
    lead_name: 'Frank DeLuca',
    lead_phone: '+15550111242',
    assigned_ai: 'ENROLL',
    last_message_at: '2026-06-20T16:00:00Z',
    unread: false,
    messages: [
      { id: 'm1', from: 'agent', via: 'ENROLL', at: '2026-06-20T16:00:00Z',
        body: 'Hi Frank — your 2027 Humana Annual Notice of Change just hit your mailbox. Some benefits are shifting. Want me to review it free and tell you stay or switch? Reply YES and I\'ll book a call.\n\nWe do not offer every plan available in your area...' },
      { id: 'm2', from: 'lead', at: '2026-06-20T17:32:00Z',
        body: 'Yes please' },
      { id: 'm3', from: 'agent', via: 'ENROLL', at: '2026-06-20T17:35:00Z',
        body: 'Friday 11am AEP review on the calendar. Bring the ANOC letter if you have it.' },
    ],
  },
];

// ── 3. Calls (8 — call log entries) ────────────────────────────
const CALLS = [
  { id: 'demo-call-001', lead_id: 'demo-lead-001', direction: 'outbound', status: 'completed', dispo: 'appt_set', started_at: '2026-06-25T20:30:00Z', duration_seconds: 412, by: 'agent' },
  { id: 'demo-call-002', lead_id: 'demo-lead-003', direction: 'outbound', status: 'completed', dispo: 'soa_sent',  started_at: '2026-06-25T15:00:00Z', duration_seconds: 287, by: 'agent' },
  { id: 'demo-call-003', lead_id: 'demo-lead-010', direction: 'outbound', status: 'completed', dispo: 'appt_set', started_at: '2026-06-20T17:36:00Z', duration_seconds: 540, by: 'agent' },
  { id: 'demo-call-004', lead_id: 'demo-lead-005', direction: 'inbound',  status: 'completed', dispo: 'payment_fix_in_progress', started_at: '2026-06-24T12:14:00Z', duration_seconds: 198, by: 'agent' },
  { id: 'demo-call-005', lead_id: 'demo-lead-009', direction: 'outbound', status: 'no-answer', dispo: 'voicemail', started_at: '2026-06-13T10:00:00Z', duration_seconds: 0, by: 'agent' },
  { id: 'demo-call-006', lead_id: 'demo-lead-002', direction: 'outbound', status: 'completed', dispo: 'follow_up',  started_at: '2026-06-23T14:20:00Z', duration_seconds: 605, by: 'agent' },
  { id: 'demo-call-007', lead_id: 'demo-lead-006', direction: 'outbound', status: 'completed', dispo: 'appt_set',   started_at: '2026-06-09T11:00:00Z', duration_seconds: 720, by: 'agent' },
  { id: 'demo-call-008', lead_id: 'demo-lead-012', direction: 'outbound', status: 'completed', dispo: 'app_started',started_at: '2026-06-18T14:00:00Z', duration_seconds: 1320,by: 'agent' },
];

// ── 4. Calendar (8 entries — past + future) ────────────────────
const CALENDAR = [
  { id: 'demo-cal-001', lead_id: 'demo-lead-010', title: 'Frank DeLuca — AEP review',          starts_at: '2026-06-26T16:00:00Z', duration_minutes: 30, status: 'confirmed',  type: 'consult' },
  { id: 'demo-cal-002', lead_id: 'demo-lead-001', title: 'John Smith — IUL illustration walk', starts_at: '2026-06-25T20:00:00Z', duration_minutes: 45, status: 'completed',  type: 'consult' },
  { id: 'demo-cal-003', lead_id: 'demo-lead-003', title: 'Maria Lopez — post-SOA plan review', starts_at: '2026-06-27T15:00:00Z', duration_minutes: 60, status: 'confirmed',  type: 'enrollment' },
  { id: 'demo-cal-004', lead_id: 'demo-lead-002', title: 'Sarah Johnson — APTC walkthrough',   starts_at: '2026-06-26T14:00:00Z', duration_minutes: 45, status: 'confirmed',  type: 'consult' },
  { id: 'demo-cal-005', lead_id: 'demo-lead-006', title: 'Patricia Williams — Medigap re-shop',starts_at: '2026-06-30T17:00:00Z', duration_minutes: 30, status: 'confirmed',  type: 'enrollment' },
  { id: 'demo-cal-006', lead_id: 'demo-lead-011', title: 'Helen Park — preventive visit reminder', starts_at: '2026-07-02T13:00:00Z', duration_minutes: 15, status: 'reminder', type: 'check_in' },
  { id: 'demo-cal-007', lead_id: 'demo-lead-005', title: 'David Cherryholmes — payment fix call', starts_at: '2026-06-26T18:00:00Z', duration_minutes: 15, status: 'confirmed',  type: 'retention' },
  { id: 'demo-cal-008', lead_id: 'demo-lead-009', title: 'Greg Anderson — re-book attempt',    starts_at: '2026-06-28T16:30:00Z', duration_minutes: 30, status: 'tentative', type: 'recovery' },
];

// ── 5. Commissions (representative book) ───────────────────────
// Mix of clean reconciled rows, recent advances, and one chargeback —
// shows the LEDGER reconciliation engine has real work to do.
const COMMISSIONS = [
  { id: 'demo-comm-001', client_name: 'John Doe Sr.',     carrier: 'Mutual of Omaha', product: 'Whole Life',     premium_monthly: 145, status: 'reconciled', amount_cents: 12300,  date: '2026-06-15', kind: 'advance' },
  { id: 'demo-comm-002', client_name: 'Mary Adams',       carrier: 'Aetna ACA',       product: 'Silver 70',      premium_monthly:  85, status: 'reconciled', amount_cents:  5400,  date: '2026-06-14', kind: 'advance' },
  { id: 'demo-comm-003', client_name: 'Roberto Mendez',   carrier: 'Humana MAPD',     product: 'Plan H 2026',    premium_monthly:   0, status: 'reconciled', amount_cents: 60100,  date: '2026-06-12', kind: 'aep_first_year' },
  { id: 'demo-comm-004', client_name: 'Jane Holloway',    carrier: 'Transamerica',    product: 'IUL · Foundation', premium_monthly: 400, status: 'reconciled', amount_cents: 28800, date: '2026-06-10', kind: 'advance' },
  { id: 'demo-comm-005', client_name: 'Sandra Park',      carrier: 'Cigna ACA',       product: 'Bronze HMO',     premium_monthly:  62, status: 'reconciled', amount_cents:  3900,  date: '2026-06-08', kind: 'advance' },
  { id: 'demo-comm-006', client_name: 'Carlos Reyes',     carrier: 'Mutual of Omaha', product: 'Medigap G',      premium_monthly: 152, status: 'reconciled', amount_cents:  9700,  date: '2026-06-07', kind: 'first_year' },
  { id: 'demo-comm-007', client_name: 'Linda Foster',     carrier: 'Foresters',       product: 'PlanRight FEX',  premium_monthly:  78, status: 'reconciled', amount_cents:  5460,  date: '2026-06-06', kind: 'advance' },
  { id: 'demo-comm-008', client_name: 'Vincent Marino',   carrier: 'AIG / Corebridge',product: 'Term · Quick',   premium_monthly:  35, status: 'reconciled', amount_cents:  1890,  date: '2026-06-04', kind: 'advance' },
  { id: 'demo-comm-009', client_name: 'Greg Walker',      carrier: 'Wellcare',        product: 'PDP Value',      premium_monthly:  18, status: 'reconciled', amount_cents:   720,  date: '2026-06-03', kind: 'first_year' },
  { id: 'demo-comm-010', client_name: 'Sharon Davis',     carrier: 'BCBS of TX',      product: 'Gold PPO',       premium_monthly: 180, status: 'reconciled', amount_cents: 11500,  date: '2026-06-01', kind: 'advance' },
  // Chargeback row — gives LEDGER something to reconcile.
  { id: 'demo-comm-011', client_name: 'Greg Walker',      carrier: 'Wellcare',        product: 'PDP Value',      premium_monthly:  18, status: 'chargeback', amount_cents:  -540, date: '2026-06-22', kind: 'chargeback', detail: 'AEP free-look surrender' },
  // Pending — the LEDGER cash-flow forecast can show this clearing.
  { id: 'demo-comm-012', client_name: 'Eric Brown',       carrier: 'Mutual of Omaha', product: 'FEX Living Promise', premium_monthly: 78, status: 'pending', amount_cents: 5460, date: '2026-06-25', kind: 'advance_pending' },
];

// ── 6. Book of business summary (25 in-force clients) ──────────
const BOOK_OF_BUSINESS = {
  in_force_count: 187,
  active_premium_monthly: 24600,
  thirteen_mo_persistency_pct: 83.4,
  chargeback_rate_pct: 4.2,
  lifetime_value_estimate: 412000,
  by_lob: [
    { lob: 'medicare',      count: 71, premium_monthly:  9800 },
    { lob: 'health_aca',    count: 54, premium_monthly:  6200 },
    { lob: 'life',          count: 32, premium_monthly:  5100 },
    { lob: 'final_expense', count: 19, premium_monthly:  2300 },
    { lob: 'dental_vision', count: 11, premium_monthly:  1200 },
  ],
  cross_sell_gaps: [
    { coverage: 'dental',  missing_count: 47, est_annual_opp_cents: 700000 },
    { coverage: 'life',    missing_count: 38, est_annual_opp_cents: 540000 },
    { coverage: 'accident',missing_count: 62, est_annual_opp_cents: 380000 },
  ],
};

// ── 7. AI Employees — production names + current activity ──────
// Per build prompt §4.3 #5: production names are canonical. Friendly
// alias kept as `friendly` for tour copy / hover.
const AI_EMPLOYEES = [
  { id: 'nova',    name: 'NOVA',    friendly: 'AI Lead Nurturer',         status: 'active',  last_action: 'Sent welcome SMS to Linda Martinez', actions_today: 47, owns: ['demo-lead-001','demo-lead-002','demo-lead-007','demo-lead-008','demo-lead-009','demo-lead-012'] },
  { id: 'enroll',  name: 'ENROLL',  friendly: 'AI Enrollment Agent',      status: 'active',  last_action: 'SOA captured for Maria Lopez (48h wait active)', actions_today: 12, owns: ['demo-lead-003','demo-lead-006','demo-lead-010'] },
  { id: 'retain',  name: 'RETAIN',  friendly: 'AI Retention Specialist',  status: 'active',  last_action: 'Detected NSF on David Cherryholmes — queued recovery call', actions_today: 8, owns: ['demo-lead-005','demo-lead-011'] },
  { id: 'anchor',  name: 'ANCHOR',  friendly: 'AI ACA Issue Resolver',    status: 'active',  last_action: 'Day-0 income verification SMS to Robert Chen', actions_today: 6, owns: ['demo-lead-004'] },
  { id: 'ledger',  name: 'LEDGER',  friendly: 'AI CFO / Reconciler',      status: 'active',  last_action: 'Flagged $540 chargeback variance on Greg Walker policy', actions_today: 3 },
  { id: 'guide',   name: 'GUIDE',   friendly: 'AI Onboarding Coach',      status: 'active',  last_action: 'Walked operator through LEDGER dashboard', actions_today: 14 },
  { id: 'audit',   name: 'AUDIT',   friendly: 'AI Compliance Auditor',    status: 'active',  last_action: 'Hash-chained 7 new audit_events from today', actions_today: 7 },
  { id: 'consent', name: 'CONSENT', friendly: 'AI Consent Enforcer',      status: 'active',  last_action: 'Blocked SMS to opted-out lead', actions_today: 1 },
  { id: 'shield',  name: 'SHIELD',  friendly: 'AI Compliance Umbrella',   status: 'active',  last_action: 'Verified A2P + quiet-hours on all outbound', actions_today: 47 },
  { id: 'ascend',  name: 'ASCEND',  friendly: 'AI Customer Success',      status: 'monitoring', last_action: 'Tracking activation progress', actions_today: 2 },
];

// ── 8. Sample workflow (for the workflow animation) ─────────────
// Medicare advantage 12-step. Demo shows the client at step 5.
const SAMPLE_WORKFLOW = {
  id: 'demo-wf-001',
  client_name: 'Maria Lopez',
  carrier: 'Humana Medicare Advantage',
  effective_date: '2026-07-26',
  current_step_index: 4,
  steps: [
    { key: 'welcome',          title: 'Welcome + Evidence of Coverage', day: 0,   channel: 'SMS + email', done: true },
    { key: 'portal_setup',     title: 'Portal registration nudge',      day: 3,   channel: 'SMS',         done: true },
    { key: 'id_card',          title: 'ID card check',                  day: 10,  channel: 'SMS',         done: true },
    { key: 'pcp_selection',    title: 'PCP selection',                  day: 14,  channel: 'SMS',         done: true },
    { key: 'prescription',     title: 'Prescription drug walkthrough',  day: 18,  channel: 'SMS',         done: false, current: true },
    { key: 'awv',              title: 'Annual Wellness Visit reminder', day: 21,  channel: 'SMS',         done: false },
    { key: 'otc',              title: 'OTC + dental + vision + fitness',day: 30,  channel: 'SMS',         done: false },
    { key: 'check_in_30d',     title: '30-day check-in',                day: 35,  channel: 'SMS',         done: false },
    { key: 'check_in_90d',     title: '90-day check-in',                day: 90,  channel: 'SMS',         done: false },
    { key: 'anoc_alert',       title: 'ANOC arrival',                   day: 270, channel: 'SMS',         done: false },
    { key: 'aep_outreach',     title: 'AEP outreach',                   day: 285, channel: 'Task',        done: false },
    { key: 'annual_review',    title: 'Annual review',                  day: 335, channel: 'Task',        done: false },
  ],
};

// ── 9. Scenarios (the one-click player from build prompt §7) ────
const SCENARIOS = [
  {
    id: 'sc-import-leads',
    title: 'Import 1,000 leads',
    blurb: 'Watch the agency triple in size in 8 seconds. Each lead is auto-scored, deduped, and routed to the right AI agent.',
    steps: [
      'Parsing CSV (1,000 rows)…',
      'Deduping against existing book (47 duplicates merged)…',
      'AI scoring: 142 hot · 391 warm · 467 cold',
      'Routing: NOVA gets 538, ENROLL gets 287, ANCHOR gets 175',
      'Welcome cadences queued (953 SMS, all consent-gated)',
      '✓ Imported in 8 seconds',
    ],
  },
  {
    id: 'sc-aca-purchase',
    title: 'Client purchases ACA plan',
    blurb: 'Sarah Johnson signs — watch the full post-sale handoff fire automatically.',
    steps: [
      'ENROLL submits application to Marketplace…',
      'CMS consent-to-assist captured + stamped to audit chain',
      'Effectuation pipeline → Pending',
      'RETAIN takes over: workflow engine queues 10-step ACA cadence',
      'Day-0 welcome SMS fires (gated by quiet hours + A2P)',
      'ANCHOR pre-arms for any HealthSherpa issues',
      '✓ Sarah is fully onboarded — $0 operator time spent',
    ],
  },
  {
    id: 'sc-missed-payment',
    title: 'Missed payment recovery',
    blurb: 'David Cherryholmes\'s draft bounces. Watch RETAIN save the policy without operator involvement.',
    steps: [
      'NSF event detected on Transamerica statement parse',
      'RETAIN: 30-day grace window opens',
      'Day-0 SMS sent (non-judgmental, fastest path to fix)',
      'Workflow Engine queues auto-call for Day-1 (canDial gated)',
      'David responds with new card → portal link sent',
      'Payment confirmed → policy preserved',
      '✓ $3,000/yr premium + $2,250 chargeback saved',
    ],
  },
  {
    id: 'sc-medicare-renewal',
    title: 'Medicare renewal (AEP)',
    blurb: 'Frank DeLuca\'s 2027 ANOC arrives. ENROLL handles the entire re-shop conversation.',
    steps: [
      'ANOC detected — benefits shifted on current Humana plan',
      'ENROLL sends SOA refresh request (SMS + email)',
      'Beneficiary signs in 4 minutes',
      '48-hour wait clock starts (CMS rule, auto-enforced)',
      'After window: ENROLL pulls 3 alternative plans matched to meds + doctors',
      'AEP review booked on calendar',
      '✓ Renewal protected; TPMO disclosure logged throughout',
    ],
  },
  {
    id: 'sc-commission-statement',
    title: 'Commission statement upload',
    blurb: 'Drop a Transamerica statement. LEDGER reconciles it penny-exact.',
    steps: [
      'Parsing PDF (43 policy rows)…',
      'Detecting carrier: Transamerica · IUL Foundation + FEX',
      'Auto-classifying status: 38 active · 3 free-look · 2 NSF',
      'Variance check vs expected: $87 underpayment on policy #IULA052122',
      'LEDGER drafts dispute language: "12.5% commission paid, contract says 13.0%"',
      'Audit-events row chained (hash-linked to prior)',
      '✓ Reconciled — operator sees the $87 LEDGER caught',
    ],
  },
  {
    id: 'sc-new-referral',
    title: 'New referral lands',
    blurb: 'Linda Martinez fills out John Smith\'s referral page. Niche-aware landing + inline booking.',
    steps: [
      'Form submit on /refer/<agent_id> — niche tag: life/IUL',
      'Lead row created with referrer attribution (John Smith)',
      'Inline calendar pick → Thursday 4pm booked instantly',
      'NOVA welcome SMS fires (consent captured at form submit)',
      'John Smith gets referrer thank-you SMS',
      'Calendar invite sent to Linda + agent',
      '✓ Zero operator clicks — referral → booked appointment in 90 seconds',
    ],
  },
];

// ── 10. Activity feed pool (for the dashboard live feed) ────────
const ACTIVITY_FEED_POOL = [
  { agent: 'NOVA',    text: 'Sent welcome SMS to a new lead' },
  { agent: 'NOVA',    text: 'Booked an appointment automatically' },
  { agent: 'NOVA',    text: 'Detected a hot lead — escalated to operator' },
  { agent: 'ENROLL',  text: 'Captured SOA signature' },
  { agent: 'ENROLL',  text: 'Submitted Medicare application' },
  { agent: 'ENROLL',  text: 'Sent ANOC review request' },
  { agent: 'RETAIN',  text: 'Flagged at-risk policy' },
  { agent: 'RETAIN',  text: 'Recovered an NSF — policy saved' },
  { agent: 'RETAIN',  text: 'Sent 90-day check-in to active client' },
  { agent: 'ANCHOR',  text: 'Closed a HealthSherpa income-doc issue' },
  { agent: 'ANCHOR',  text: 'Detected AOR transfer request' },
  { agent: 'LEDGER',  text: 'Reconciled a carrier statement penny-exact' },
  { agent: 'LEDGER',  text: 'Flagged a $87 commission variance' },
  { agent: 'AUDIT',   text: 'Chained a new audit_events row (immutable)' },
  { agent: 'CONSENT', text: 'Blocked SMS — lead is opted out' },
  { agent: 'SHIELD',  text: 'Verified A2P + quiet hours on outbound batch' },
  { agent: 'GUIDE',   text: 'Walked operator through new feature' },
];

// ── 11. Health clients (15) ─────────────────────────────────────
// Mix of ACA marketplace + Medicare Advantage + Medigap. Each has
// last-review dates so at-risk detection surfaces the stale ones.
const HEALTH_CLIENTS = [
  { id: 'demo-hclient-001', name: 'Robert Chen',     phone: '+15550120101', email: 'robert.chen@example.com',     state: 'FL', dob: '1959-03-12', niche: 'medicare',   plan_type: 'medicare_advantage', carrier: 'UnitedHealthcare', last_contact_at: '2026-05-15', at_risk: false },
  { id: 'demo-hclient-002', name: 'Linda Martinez',  phone: '+15550120102', email: 'linda.martinez@example.com',  state: 'TX', dob: '1962-07-22', niche: 'medicare',   plan_type: 'medicare_supplement', carrier: 'Aetna',           last_contact_at: '2026-04-02', at_risk: true },
  { id: 'demo-hclient-003', name: 'James Wright',    phone: '+15550120103', email: 'j.wright@example.net',        state: 'AZ', dob: '1974-11-05', niche: 'aca',        plan_type: 'aca_marketplace',    carrier: 'BCBS',            last_contact_at: '2026-06-20', at_risk: false },
  { id: 'demo-hclient-004', name: 'Emily Foster',    phone: '+15550120104', email: 'emily.f@example.com',         state: 'GA', dob: '1981-09-18', niche: 'aca',        plan_type: 'aca_marketplace',    carrier: 'Ambetter',        last_contact_at: '2026-02-11', at_risk: true },
  { id: 'demo-hclient-005', name: 'Michael Torres',  phone: '+15550120105', email: 'mtorres@example.org',         state: 'FL', dob: '1958-01-30', niche: 'medicare',   plan_type: 'medicare_advantage', carrier: 'Humana',          last_contact_at: '2026-06-01', at_risk: false },
  { id: 'demo-hclient-006', name: 'Patricia Nguyen', phone: '+15550120106', email: 'p.nguyen@example.com',        state: 'NC', dob: '1969-05-14', niche: 'aca',        plan_type: 'aca_marketplace',    carrier: 'BCBS',            last_contact_at: '2026-05-28', at_risk: false },
  { id: 'demo-hclient-007', name: 'David Kim',       phone: '+15550120107', email: 'david.kim@example.com',       state: 'TX', dob: '1955-08-09', niche: 'medicare',   plan_type: 'medicare_pdp',       carrier: 'WellCare',        last_contact_at: '2026-03-18', at_risk: true },
  { id: 'demo-hclient-008', name: 'Karen Adams',     phone: '+15550120108', email: 'karen.adams@example.com',     state: 'SC', dob: '1972-12-01', niche: 'aca',        plan_type: 'aca_marketplace',    carrier: 'Cigna',           last_contact_at: '2026-06-25', at_risk: false },
  { id: 'demo-hclient-009', name: 'Thomas Rivera',   phone: '+15550120109', email: 't.rivera@example.net',        state: 'AZ', dob: '1960-04-25', niche: 'medicare',   plan_type: 'medicare_advantage', carrier: 'UnitedHealthcare', last_contact_at: '2026-01-10', at_risk: true },
  { id: 'demo-hclient-010', name: 'Sandra Bell',     phone: '+15550120110', email: 'sandra.bell@example.com',     state: 'FL', dob: '1988-02-17', niche: 'aca',        plan_type: 'aca_marketplace',    carrier: 'BCBS',            last_contact_at: '2026-06-15', at_risk: false },
  { id: 'demo-hclient-011', name: 'Christopher Ford', phone: '+15550120111', email: 'chris.ford@example.com',     state: 'GA', dob: '1957-10-08', niche: 'medicare',   plan_type: 'medicare_supplement', carrier: 'Mutual of Omaha', last_contact_at: '2026-04-30', at_risk: false },
  { id: 'demo-hclient-012', name: 'Rachel Green',    phone: '+15550120112', email: 'rgreen@example.org',          state: 'TN', dob: '1978-06-19', niche: 'aca',        plan_type: 'aca_marketplace',    carrier: 'Oscar',           last_contact_at: '2026-05-05', at_risk: false },
  { id: 'demo-hclient-013', name: 'Kevin Patel',     phone: '+15550120113', email: 'k.patel@example.com',         state: 'NC', dob: '1964-11-27', niche: 'medicare',   plan_type: 'medicare_advantage', carrier: 'Aetna',           last_contact_at: '2026-06-10', at_risk: false },
  { id: 'demo-hclient-014', name: 'Nicole Barnes',   phone: '+15550120114', email: 'n.barnes@example.com',        state: 'FL', dob: '1966-08-03', niche: 'medicare',   plan_type: 'medicare_supplement', carrier: 'BCBS',            last_contact_at: '2026-06-22', at_risk: false },
  { id: 'demo-hclient-015', name: 'Anthony Silva',   phone: '+15550120115', email: 'a.silva@example.net',         state: 'TX', dob: '1983-01-15', niche: 'aca',        plan_type: 'aca_marketplace',    carrier: 'Ambetter',        last_contact_at: '2026-02-28', at_risk: true },
];

// ── 12. Life clients (14) ────────────────────────────────────────
// Doubles as the seed for demoClients() in liveAdapters.js — shaped
// with the extra fields (policy #, billing_status/mode, draft_amount,
// issue_date, first_draft_date, address/state_abbr, vertical) that
// the real `clients` table carries and Book of Business / ClientsView
// read (see src/cockpit/BookOfBusinessBoard.jsx). policy_type /
// carrier / face_amount / monthly_premium stay for display + feed the
// matching entries in LIFE_BOOK_TRANSACTIONS below (kept
// internally consistent by name).
const LIFE_CLIENTS = [
  { id: 'demo-lclient-001', name: 'John Anderson',   phone: '+15550130101', email: 'j.anderson@example.com', address: '210 Birchwood Dr', state: 'TX', state_abbr: 'TX', dob: '1970-04-12', policy_type: 'iul',            carrier: 'F&G',              face_amount: 250000, monthly_premium: 285, last_contact_at: '2026-05-20',
    policy: 'FG-IU-40211', billing_status: 'active',  billing_mode: 'monthly', draft_amount: 285, issue_date: '2024-11-02', first_draft_date: '2024-12-02', vertical: 'life' },
  { id: 'demo-lclient-002', name: 'Susan Cole',      phone: '+15550130102', email: 'susan.cole@example.com', address: '88 Palmetto Ct',   state: 'FL', state_abbr: 'FL', dob: '1978-09-05', policy_type: 'term_20',        carrier: 'Mutual of Omaha',  face_amount: 500000, monthly_premium: 45,  last_contact_at: '2026-06-01',
    policy: 'MOO-TE-10387', planCode: 'TE20', billing_status: 'active',  billing_mode: 'monthly', draft_amount: 45,  issue_date: '2025-01-14', first_draft_date: '2025-02-14', vertical: 'life' },
  { id: 'demo-lclient-003', name: 'Mark Jefferson',  phone: '+15550130103', email: 'mark.j@example.org',     address: '4 Loganberry Ln',  state: 'GA', state_abbr: 'GA', dob: '1965-11-18', policy_type: 'whole_life',     carrier: 'Americo',          face_amount: 100000, monthly_premium: 120, last_contact_at: '2026-04-15',
    policy: 'AMC-WL-51029', billing_status: 'active',  billing_mode: 'monthly', draft_amount: 120, issue_date: '2024-06-20', first_draft_date: '2024-07-20', vertical: 'life' },
  { id: 'demo-lclient-004', name: 'Jennifer Kim',    phone: '+15550130104', email: 'jkim@example.com',       address: '19 Saguaro Way',   state: 'AZ', state_abbr: 'AZ', dob: '1982-02-28', policy_type: 'term_30',        carrier: 'Transamerica',     face_amount: 750000, monthly_premium: 62,  last_contact_at: '2026-06-10',
    policy: 'TAM-77021',   billing_status: 'active',  billing_mode: 'monthly', draft_amount: 62,  issue_date: '2025-03-04', first_draft_date: '2025-04-04', vertical: 'life' },
  { id: 'demo-lclient-005', name: 'Charles Ross',    phone: '+15550130105', email: 'c.ross@example.com',     address: '502 Magnolia Ave', state: 'TN', state_abbr: 'TN', dob: '1955-07-14', policy_type: 'final_expense',  carrier: 'Foresters',        face_amount: 25000,  monthly_premium: 68,  last_contact_at: '2026-05-01',
    policy: 'FOR-FE-88104', billing_status: 'lapsed',  billing_mode: 'monthly', draft_amount: 68,  issue_date: '2024-02-11', first_draft_date: '2024-03-11', vertical: 'life' },
  { id: 'demo-lclient-006', name: 'Angela Martinez', phone: '+15550130106', email: 'a.martinez@example.com', address: '77 Bayshore Dr',   state: 'FL', state_abbr: 'FL', dob: '1972-10-02', policy_type: 'iul',            carrier: 'Prosperity',       face_amount: 500000, monthly_premium: 425, last_contact_at: '2026-06-18',
    policy: 'PRO-IU-33012', billing_status: 'active',  billing_mode: 'monthly', draft_amount: 425, issue_date: '2025-05-09', first_draft_date: '2025-06-09', vertical: 'life' },
  { id: 'demo-lclient-007', name: 'Brian Walker',    phone: '+15550130107', email: 'brian.w@example.net',    address: '306 Millbrook Rd', state: 'NC', state_abbr: 'NC', dob: '1968-03-24', policy_type: 'whole_life',     carrier: 'F&G',              face_amount: 150000, monthly_premium: 155, last_contact_at: '2026-05-11',
    policy: 'FG-WL-40355', billing_status: 'active',  billing_mode: 'monthly', draft_amount: 155, issue_date: '2024-09-17', first_draft_date: '2024-10-17', vertical: 'life' },
  { id: 'demo-lclient-008', name: 'Michelle Hayes',  phone: '+15550130108', email: 'm.hayes@example.com',    address: '15 Cottonwood St', state: 'TX', state_abbr: 'TX', dob: '1985-12-08', policy_type: 'term_20',        carrier: 'Mutual of Omaha',  face_amount: 350000, monthly_premium: 38,  last_contact_at: '2026-06-25',
    policy: 'MOO-TE-10402', planCode: 'TE20', billing_status: 'nsf',     billing_mode: 'monthly', draft_amount: 38,  issue_date: '2025-08-01', first_draft_date: '2025-09-01', vertical: 'life' },
  { id: 'demo-lclient-009', name: 'Ronald Perez',    phone: '+15550130109', email: 'r.perez@example.org',    address: '620 Harbor View',  state: 'SC', state_abbr: 'SC', dob: '1959-06-30', policy_type: 'final_expense',  carrier: 'Americo',          face_amount: 20000,  monthly_premium: 55,  last_contact_at: '2026-03-20',
    policy: 'AMC-FE-51188', billing_status: 'pending', billing_mode: 'monthly', draft_amount: 55,  issue_date: '2026-06-01', first_draft_date: '2026-07-01', vertical: 'life' },
  { id: 'demo-lclient-010', name: 'Melissa Cooper',  phone: '+15550130110', email: 'melissa.c@example.com',  address: '9 Willowbend Ct',  state: 'GA', state_abbr: 'GA', dob: '1976-08-16', policy_type: 'iul',            carrier: 'Transamerica',     face_amount: 400000, monthly_premium: 320, last_contact_at: '2026-06-05',
    policy: 'IULA-88213', billing_status: 'active',  billing_mode: 'monthly', draft_amount: 320, issue_date: '2025-02-22', first_draft_date: '2025-03-22', vertical: 'life' },
  { id: 'demo-lclient-011', name: 'Steven Ortiz',    phone: '+15550130111', email: 'steven.o@example.com',   address: '48 Desert Bloom',  state: 'AZ', state_abbr: 'AZ', dob: '1961-01-22', policy_type: 'whole_life',     carrier: 'Foresters',        face_amount: 75000,  monthly_premium: 95,  last_contact_at: '2026-04-08',
    policy: 'FOR-WL-88220', billing_status: 'active',  billing_mode: 'monthly', draft_amount: 95,  issue_date: '2024-12-05', first_draft_date: '2025-01-05', vertical: 'life' },
  { id: 'demo-lclient-012', name: 'Amanda Reed',     phone: '+15550130112', email: 'a.reed@example.net',     address: '133 Egret Landing',state: 'FL', state_abbr: 'FL', dob: '1980-11-11', policy_type: 'term_30',        carrier: 'F&G',              face_amount: 600000, monthly_premium: 58,  last_contact_at: '2026-06-15',
    policy: 'FG-TE-40488', billing_status: 'active',  billing_mode: 'monthly', draft_amount: 58,  issue_date: '2025-06-30', first_draft_date: '2025-07-30', vertical: 'life' },
  { id: 'demo-lclient-013', name: 'Dorothy Blake',   phone: '+15550130113', email: 'd.blake@example.com',    address: '77 Redstone Way',  state: 'OH', state_abbr: 'OH', dob: '1958-02-19', policy_type: 'final_expense',  carrier: 'Transamerica',     face_amount: 15000,  monthly_premium: 58,  last_contact_at: '2026-06-12',
    policy: 'FEXB-77210', billing_status: 'active',  billing_mode: 'monthly', draft_amount: 58,  issue_date: '2025-09-08', first_draft_date: '2025-10-08', vertical: 'life' },
  { id: 'demo-lclient-014', name: 'Walter Higgins',  phone: '+15550130114', email: 'w.higgins@example.org',  address: '5 Cardinal Cir',   state: 'NC', state_abbr: 'NC', dob: '1969-05-27', policy_type: 'iul',            carrier: 'Mutual of Omaha',  face_amount: 450000, monthly_premium: 340, last_contact_at: '2026-06-28',
    policy: 'MOO-IU-10599', planCode: 'IXEXP987', billing_status: 'active',  billing_mode: 'monthly', draft_amount: 340, issue_date: '2025-04-16', first_draft_date: '2025-05-16', vertical: 'life' },
];

// ── 12b. Life book of business — extra "book" transactions (34) ──
// Additional policies beyond the 14 named LIFE_CLIENTS above, so the
// Life Command Center's book-of-business KPIs (Total Annual Premium,
// Policies In Force, Top Carriers, Commissions Earned) look like a
// real working book rather than a 14-row toy. These aren't backed by
// their own `clients` rows — same relationship real commission
// statements have to the client roster (many more historical
// policies flow through statements than sit in the active roster).
// Deterministic (no Math.random) so the seed is stable across renders.
const LIFE_BOOK_FILLER_FIRST = ['James','Linda','Robert','Barbara','Michael','Susan','William','Jessica','David','Karen','Richard','Nancy','Joseph','Betty','Thomas','Sandra','Charles','Ashley','Anthony','Michelle','Mark','Carol','Paul','Amanda','Kevin','Donna','Edward','Laura','Jason','Rebecca','Jeffrey','Sharon','Ryan','Cynthia'];
const LIFE_BOOK_FILLER_LAST  = ['Turner','Coleman','Barnes','Fisher','Henderson','Gordon','Wallace','Woods','Cole','West','Jordan','Owens','Fox','Warren','Hunt','Black','Daniels','Palmer','Mills','Nichols','Knight','Hayes','Byrd','Reid','Wolfe','Chapman','Fields','Doyle','Sutton','Vega','Ramsey','Holt','Dunn','Pratt'];
// Templates chosen to hit real resolveCarrier/resolveProduct legacy
// fallback branches (see resolveCarrier/resolveProduct in
// CommissionTracker.jsx) so the "Policies by Type" donut gets real,
// non-fabricated-bucket slices — same rule the live book uses.
const LIFE_BOOK_TEMPLATES = [
  { carrier: 'Mutual of Omaha',      planCode: 'FEPL',   premiumBase: 65,  premiumSpan: 28 }, // -> Final Expense
  { carrier: 'Mutual of Omaha',      planCode: 'TE20',   premiumBase: 42,  premiumSpan: 26 }, // -> Term
  { carrier: 'Mutual of Omaha',      planCode: 'IXEXP',  premiumBase: 290, premiumSpan: 160 }, // -> IUL
  { carrier: 'Transamerica',         policyPrefix: 'IULA', premiumBase: 265, premiumSpan: 170 }, // -> IUL
  { carrier: 'Transamerica',         policyPrefix: 'FEXB', premiumBase: 58,  premiumSpan: 34 },  // -> Final Expense
  { carrier: 'North American Company', policyPrefix: 'NACL', premiumBase: 115, premiumSpan: 100 },  // -> Other (real carrier, no legacy product rule)
  { carrier: 'North American Company', policyPrefix: 'NAIU', premiumBase: 275, premiumSpan: 150 }, // -> Other
  { carrier: 'Protective Life',      policyPrefix: 'PROT', premiumBase: 38,  premiumSpan: 28 },  // -> Other
  { carrier: 'Protective Life',      policyPrefix: 'PRUL', premiumBase: 140, premiumSpan: 110 }, // -> Other
  { carrier: 'Lincoln Financial',    policyPrefix: 'LFWL', premiumBase: 110, premiumSpan: 90 },  // -> Other
];
function monthOffsetDate(monthsAgo, day = 15) {
  // Fixed "today" anchor (2026-07-15) — matches the late-June/early-
  // July 2026 "today" the rest of this seed (LEADS/CALENDAR/
  // COMMISSIONS) is already anchored to. Static, not Date.now()-based,
  // so this module doesn't reshuffle if evaluated on a different day.
  const d = new Date(Date.UTC(2026, 6, day));
  d.setUTCMonth(d.getUTCMonth() - monthsAgo);
  return d.toISOString().slice(0, 10);
}
const LIFE_BOOK_FILLER = Array.from({ length: 34 }, (_, i) => {
  const t = LIFE_BOOK_TEMPLATES[i % LIFE_BOOK_TEMPLATES.length];
  const first = LIFE_BOOK_FILLER_FIRST[i % LIFE_BOOK_FILLER_FIRST.length];
  const last = LIFE_BOOK_FILLER_LAST[(i * 7) % LIFE_BOOK_FILLER_LAST.length];
  const premium = t.premiumBase + ((i * 13) % (t.premiumSpan + 1));
  const cycle = Math.floor(i / LIFE_BOOK_TEMPLATES.length); // 0,1,2,3 — keeps policy #s unique across repeats
  const policyBase = t.policyPrefix || t.planCode;
  return {
    insured: `${first} ${last}`,
    company: t.carrier,
    planCode: t.planCode || undefined,
    policy: `${policyBase}${20000 + i * 37 + cycle}`,
    monthlyPremium: premium,
    activityDate: monthOffsetDate(1 + (i % 13)),
    isChargeback: false,
  };
});

// ── 12c. Life book transactions — the 14 named clients + filler ──
// Each named client gets exactly one posted "advance" commission
// transaction matching its own policy/carrier/premium (kept
// consistent with the LIFE_CLIENTS row above by name + policy #), so
// a client's book-of-business row and its Life Command Center
// commission entry are the same underlying policy. Two of the named
// clients (the lapsed + nsf ones) also get a chargeback row, giving
// LEDGER/persistency something real to compute against.
const LIFE_BOOK_TRANSACTIONS = [
  ...LIFE_CLIENTS.map((c) => ({
    insured: c.name,
    company: c.carrier,
    planCode: c.planCode || undefined,
    policy: c.policy,
    monthlyPremium: c.monthly_premium,
    faceAmount: c.face_amount,
    activityDate: c.issue_date,
    isChargeback: false,
  })),
  // Chargebacks — Charles Ross (lapsed) and Michelle Hayes (nsf).
  { insured: 'Charles Ross',   company: 'Foresters',       policy: 'FOR-FE-88104', monthlyPremium: 68, activityDate: '2026-04-02', isChargeback: true },
  { insured: 'Michelle Hayes', company: 'Mutual of Omaha', planCode: 'TE20', policy: 'MOO-TE-10402', monthlyPremium: 38, activityDate: '2026-06-30', isChargeback: true },
  ...LIFE_BOOK_FILLER,
];

// ── 12d. Life book conversations (8) — subset of LIFE_CLIENTS ────
// Feeds demoLifeThreads() in liveAdapters.js -> the Messaging Command
// Center. Each references a real name/phone/email from LIFE_CLIENTS
// above so client -> policy -> conversation stay consistent.
const LIFE_BOOK_THREADS = [
  { client_ref: 'demo-lclient-001', last_message: 'Got the illustration, looks great — when does the first draft come out?', unread: true,  last_direction: 'inbound',  last_ai: false, minutesAgo: 40, channel: 'sms' },
  { client_ref: 'demo-lclient-002', last_message: 'Reminder: your Mutual of Omaha term draft posts on the 14th. Reply if your card changed.', unread: false, last_direction: 'outbound', last_ai: true,  minutesAgo: 130, channel: 'sms' },
  { client_ref: 'demo-lclient-005', last_message: 'Hey Charles — your Foresters draft came back unpaid. 30-day grace window, want me to text the portal link?', unread: true, last_direction: 'outbound', last_ai: true, minutesAgo: 22, channel: 'sms' },
  { client_ref: 'demo-lclient-006', last_message: 'Yes, let\'s go ahead and bump the IUL contribution starting next quarter.', unread: false, last_direction: 'inbound', last_ai: false, minutesAgo: 320, channel: 'sms' },
  { client_ref: 'demo-lclient-008', last_message: 'Subject: Mutual of Omaha payment issue\n\nHi Michelle — your term policy draft was returned NSF. No rush, but reply here or call anytime to get it sorted before the grace period ends.', unread: true, last_direction: 'outbound', last_ai: true, minutesAgo: 900, channel: 'email' },
  { client_ref: 'demo-lclient-010', last_message: 'Perfect, Friday at 3pm works for the annual policy review.', unread: false, last_direction: 'inbound', last_ai: false, minutesAgo: 60 * 24 * 2, channel: 'sms' },
  { client_ref: 'demo-lclient-013', last_message: 'Left a voicemail — following up on the final expense beneficiary update you asked about.', unread: false, last_direction: 'outbound', last_ai: false, minutesAgo: 60 * 5, channel: 'voicemail' },
  { client_ref: 'demo-lclient-014', last_message: 'Thanks for confirming — IUL illustration refresh is on its way to your inbox now.', unread: false, last_direction: 'outbound', last_ai: true, minutesAgo: 60 * 8, channel: 'sms' },
];

// ── 13. Health policies (25 — powers War Room / MRR analytics) ──
// Each row mirrors the health_policies table shape: carrier,
// monthly_commission (agent's cut), status, renewal_date, plan_type.
const HEALTH_POLICIES = [
  // UHC concentration cluster (drives the concentration risk banner)
  { id: 'demo-hpol-001', client_id: 'demo-hclient-001', carrier: 'UnitedHealthcare', plan_name: 'AARP Medicare Advantage Choice', plan_type: 'medicare_advantage', status: 'active',  monthly_commission: 42, effective_date: '2025-01-01', renewal_date: '2026-12-31', member_id: 'MA00101' },
  { id: 'demo-hpol-002', client_id: 'demo-hclient-005', carrier: 'Humana',           plan_name: 'HumanaChoice PPO',              plan_type: 'medicare_advantage', status: 'active',  monthly_commission: 38, effective_date: '2025-01-01', renewal_date: '2026-12-31', member_id: 'MA00105' },
  { id: 'demo-hpol-003', client_id: 'demo-hclient-002', carrier: 'Aetna',            plan_name: 'Aetna Supplement Plan G',        plan_type: 'medicare_supplement', status: 'active',  monthly_commission: 55, effective_date: '2024-08-15', renewal_date: '2026-08-15', member_id: 'MS00201' },
  { id: 'demo-hpol-004', client_id: 'demo-hclient-003', carrier: 'BCBS',             plan_name: 'Blue Advantage Silver',          plan_type: 'aca_marketplace',   status: 'active',  monthly_commission: 22, effective_date: '2026-01-01', renewal_date: '2026-12-31', member_id: 'ACA00301' },
  { id: 'demo-hpol-005', client_id: 'demo-hclient-004', carrier: 'Ambetter',         plan_name: 'Ambetter Balanced Care 12',      plan_type: 'aca_marketplace',   status: 'active',  monthly_commission: 18, effective_date: '2026-01-01', renewal_date: '2026-12-31', member_id: 'ACA00401' },
  { id: 'demo-hpol-006', client_id: 'demo-hclient-006', carrier: 'BCBS',             plan_name: 'Blue Advantage Gold',            plan_type: 'aca_marketplace',   status: 'active',  monthly_commission: 25, effective_date: '2026-01-01', renewal_date: '2026-12-31', member_id: 'ACA00601' },
  { id: 'demo-hpol-007', client_id: 'demo-hclient-007', carrier: 'WellCare',         plan_name: 'WellCare Rx Value Plus',         plan_type: 'medicare_pdp',      status: 'active',  monthly_commission: 15, effective_date: '2025-01-01', renewal_date: '2026-12-31', member_id: 'PDP00701' },
  { id: 'demo-hpol-008', client_id: 'demo-hclient-008', carrier: 'Cigna',            plan_name: 'Cigna Connect Bronze',           plan_type: 'aca_marketplace',   status: 'active',  monthly_commission: 20, effective_date: '2026-01-01', renewal_date: '2026-12-31', member_id: 'ACA00801' },
  { id: 'demo-hpol-009', client_id: 'demo-hclient-009', carrier: 'UnitedHealthcare', plan_name: 'AARP Medicare Advantage Focus',  plan_type: 'medicare_advantage', status: 'active',  monthly_commission: 40, effective_date: '2025-01-01', renewal_date: '2026-12-31', member_id: 'MA00901' },
  { id: 'demo-hpol-010', client_id: 'demo-hclient-010', carrier: 'BCBS',             plan_name: 'Blue Advantage Bronze',          plan_type: 'aca_marketplace',   status: 'active',  monthly_commission: 15, effective_date: '2026-01-01', renewal_date: '2026-12-31', member_id: 'ACA01001' },
  { id: 'demo-hpol-011', client_id: 'demo-hclient-011', carrier: 'Mutual of Omaha',  plan_name: 'MoO Medigap Plan N',             plan_type: 'medicare_supplement', status: 'active',  monthly_commission: 48, effective_date: '2024-11-01', renewal_date: '2026-11-01', member_id: 'MS01101' },
  { id: 'demo-hpol-012', client_id: 'demo-hclient-012', carrier: 'Oscar',            plan_name: 'Oscar Simple Silver',            plan_type: 'aca_marketplace',   status: 'active',  monthly_commission: 22, effective_date: '2026-01-01', renewal_date: '2026-12-31', member_id: 'ACA01201' },
  { id: 'demo-hpol-013', client_id: 'demo-hclient-013', carrier: 'Aetna',            plan_name: 'Aetna Medicare Prime',           plan_type: 'medicare_advantage', status: 'active',  monthly_commission: 35, effective_date: '2025-01-01', renewal_date: '2026-12-31', member_id: 'MA01301' },
  { id: 'demo-hpol-014', client_id: 'demo-hclient-014', carrier: 'BCBS',             plan_name: 'BCBS Medigap Plan G',            plan_type: 'medicare_supplement', status: 'active',  monthly_commission: 52, effective_date: '2024-09-01', renewal_date: '2026-09-01', member_id: 'MS01401' },
  { id: 'demo-hpol-015', client_id: 'demo-hclient-015', carrier: 'Ambetter',         plan_name: 'Ambetter Value Silver',          plan_type: 'aca_marketplace',   status: 'active',  monthly_commission: 18, effective_date: '2026-01-01', renewal_date: '2026-12-31', member_id: 'ACA01501' },
  // Second policies for cross-sell demonstration (some clients have multiple)
  { id: 'demo-hpol-016', client_id: 'demo-hclient-001', carrier: 'UnitedHealthcare', plan_name: 'AARP Medicare Rx Saver',          plan_type: 'medicare_pdp',      status: 'active',  monthly_commission: 12, effective_date: '2025-01-01', renewal_date: '2026-12-31', member_id: 'PDP01601' },
  { id: 'demo-hpol-017', client_id: 'demo-hclient-002', carrier: 'Delta Dental',     plan_name: 'DeltaCare Basic',                 plan_type: 'dental',            status: 'active',  monthly_commission: 8,  effective_date: '2025-06-01', renewal_date: '2026-06-01', member_id: 'DEN01701' },
  { id: 'demo-hpol-018', client_id: 'demo-hclient-005', carrier: 'Humana',           plan_name: 'Humana Rx Value',                 plan_type: 'medicare_pdp',      status: 'active',  monthly_commission: 10, effective_date: '2025-01-01', renewal_date: '2026-12-31', member_id: 'PDP01801' },
  // Pending (recent enrollments awaiting effectuation)
  { id: 'demo-hpol-019', client_id: 'demo-hclient-006', carrier: 'BCBS',             plan_name: 'Blue Vision Basic',               plan_type: 'vision',            status: 'pending', monthly_commission: 5,  effective_date: '2026-07-01', renewal_date: '2026-12-31', member_id: 'VIS01901' },
  { id: 'demo-hpol-020', client_id: 'demo-hclient-013', carrier: 'Aetna',            plan_name: 'Aetna Rx Saver',                  plan_type: 'medicare_pdp',      status: 'pending', monthly_commission: 11, effective_date: '2026-07-01', renewal_date: '2026-12-31', member_id: 'PDP02001' },
  // Lapsed + cancelled (for book health denominator)
  { id: 'demo-hpol-021', client_id: 'demo-hclient-004', carrier: 'Ambetter',         plan_name: 'Ambetter Bronze 5',               plan_type: 'aca_marketplace',   status: 'lapsed',  monthly_commission: 0,  effective_date: '2025-01-01', renewal_date: '2025-12-31', member_id: 'ACA02101' },
  { id: 'demo-hpol-022', client_id: 'demo-hclient-007', carrier: 'WellCare',         plan_name: 'WellCare Classic',                plan_type: 'medicare_pdp',      status: 'cancelled',monthly_commission: 0, effective_date: '2024-01-01', renewal_date: '2024-12-31', member_id: 'PDP02201' },
  { id: 'demo-hpol-023', client_id: 'demo-hclient-009', carrier: 'UnitedHealthcare', plan_name: 'AARP Rx Preferred',               plan_type: 'medicare_pdp',      status: 'lapsed',  monthly_commission: 0,  effective_date: '2024-01-01', renewal_date: '2024-12-31', member_id: 'PDP02301' },
  // More UHC to push concentration >40% for war-room WARN banner
  { id: 'demo-hpol-024', client_id: 'demo-hclient-005', carrier: 'UnitedHealthcare', plan_name: 'AARP Choice Plus',                plan_type: 'medicare_advantage', status: 'active',  monthly_commission: 45, effective_date: '2025-01-01', renewal_date: '2026-12-31', member_id: 'MA02401' },
  { id: 'demo-hpol-025', client_id: 'demo-hclient-013', carrier: 'UnitedHealthcare', plan_name: 'AARP Focus HMO',                  plan_type: 'medicare_advantage', status: 'active',  monthly_commission: 40, effective_date: '2025-01-01', renewal_date: '2026-12-31', member_id: 'MA02501' },
];

// ── 14. Referral partners (8 — 2 flagged cold) ─────────────────
const REFERRAL_PARTNERS = [
  { id: 'demo-rp-001', name: 'Dr. Sarah Miller',    org: "St. Mary's Family Practice", category: 'doctor_office',      email: 's.miller@stmarys.example',  phone: '+15550140101', priority: 200, status: 'active', specialties: ['medicare','aca'], total_referrals: 12, converted_referrals: 8, last_referral_at: '2026-06-20', last_touch_at: '2026-06-15' },
  { id: 'demo-rp-002', name: 'Robert Kaplan',       org: 'Kaplan Wealth Advisors',      category: 'financial_advisor',  email: 'rob@kaplan.example',        phone: '+15550140102', priority: 180, status: 'active', specialties: ['life','iul'],    total_referrals: 8,  converted_referrals: 6, last_referral_at: '2026-06-10', last_touch_at: '2026-06-25' },
  { id: 'demo-rp-003', name: 'Lisa Chen',           org: 'Sunset Realty Group',          category: 'real_estate_agent',  email: 'lisa@sunsetrealty.example', phone: '+15550140103', priority: 150, status: 'active', specialties: ['life'],         total_referrals: 5,  converted_referrals: 3, last_referral_at: '2026-06-01', last_touch_at: '2026-06-05' },
  { id: 'demo-rp-004', name: 'Marcus Johnson',      org: 'SHIP Medicare Volunteer',      category: 'medicare_counselor', email: 'marcus@ship.example',       phone: '+15550140104', priority: 220, status: 'active', specialties: ['medicare'],     total_referrals: 15, converted_referrals: 12, last_referral_at: '2026-06-22', last_touch_at: '2026-06-20' },
  // Cold — no referral in 60+ days
  { id: 'demo-rp-005', name: 'Amanda Torres',       org: 'CorpBenefits HR Consulting',   category: 'hr_department',      email: 'a.torres@corpbenefits.example', phone: '+15550140105', priority: 140, status: 'active', specialties: ['aca'],       total_referrals: 3,  converted_referrals: 2, last_referral_at: '2026-04-01', last_touch_at: '2026-04-10' },
  // Cold — never referred
  { id: 'demo-rp-006', name: 'Mike Thomas',         org: 'Community Senior Center',      category: 'community_org',       email: 'mike@seniorcenter.example',  phone: '+15550140106', priority: 100, status: 'active', specialties: ['medicare'],  total_referrals: 0,  converted_referrals: 0, last_referral_at: null,        last_touch_at: '2026-05-15' },
  { id: 'demo-rp-007', name: 'Jennifer Park, CPA',   org: 'Park Tax & Advisory',          category: 'cpa',                 email: 'jen@parkcpa.example',        phone: '+15550140107', priority: 160, status: 'active', specialties: ['life','iul'],total_referrals: 6,  converted_referrals: 4, last_referral_at: '2026-06-18', last_touch_at: '2026-06-01' },
  { id: 'demo-rp-008', name: 'Elder Law Associates', org: 'Bell & Rowe Elder Law',        category: 'attorney',            email: 'intake@bellrowe.example',    phone: '+15550140108', priority: 170, status: 'paused', specialties: ['medicare'], total_referrals: 4,  converted_referrals: 3, last_referral_at: '2026-05-20', last_touch_at: '2026-05-25' },
];

// ── 15. Enrich existing LEADS with source_tag + lead_score ──────
// Non-destructive — new fields tacked onto every seed lead so the
// Priority Queue tab has a realistic view immediately. Score
// distribution roughly matches what the production scoring engine
// would produce: T64 Medicare referrals ~700s, cold ACA leads ~200s.
const SCORED_LEADS = LEADS.map((l, i) => {
  const isReferral   = i % 4 === 0;
  const isFbAd       = i % 3 === 1;
  const isSep        = i % 6 === 2;
  const source_tag   = isReferral ? 'referral_from_client'
                    : isSep       ? 'sep_trigger'
                    : isFbAd      ? 'facebook_ad'
                    :               'purchased_list';
  // Base score from niche + source; boosted by age proximity to T65.
  let score = 60;
  if (l.vertical === 'life')     score += 180;
  if (l.vertical === 'health')   score += 130;
  if (l.leadType === 'medicare') score += 220;
  if (source_tag === 'referral_from_client') score += 200;
  else if (source_tag === 'sep_trigger')     score += 170;
  else if (source_tag === 'facebook_ad')     score += 110;
  else                                       score += 80;
  const age = l.age || 40;
  if (age >= 64 && age < 65) score += 200;
  else if (age >= 63.5)      score += 150;
  else if (age >= 55)        score += 30;
  score = Math.max(0, Math.min(1000, score));
  return {
    ...l,
    source_tag,
    lead_score: score,
    lead_score_breakdown: {
      niche_ltv:      l.vertical === 'life' ? 180 : l.vertical === 'health' ? 130 : 60,
      source_quality: source_tag === 'referral_from_client' ? 200 : source_tag === 'sep_trigger' ? 170 : source_tag === 'facebook_ad' ? 110 : 80,
      age_band:       age >= 64 && age < 65 ? 200 : age >= 63.5 ? 150 : age >= 55 ? 30 : 0,
      consent:        25,
      state:          ['FL','TX','AZ','GA','NC'].includes(l.state) ? 40 : 0,
      engagement:     0,
      penalties:      0,
    },
    stage:                    l.stage === 'appointment_set' ? 'booked' : (l.stage === 'sold' ? 'sold' : 'new'),
    phone_e164:               l.phone,
    state_code:               l.state,
    first_contact_at:         i % 3 === 0 ? null : l.createdAt,
    speed_to_contact_seconds: i % 3 === 0 ? null : (60 + i * 45),
    created_at:               l.createdAt,
  };
});

// ── Public export ──────────────────────────────────────────────
const DEMO_SEED = {
  leads: SCORED_LEADS,
  inbox_threads: INBOX_THREADS,
  calls: CALLS,
  calendar: CALENDAR,
  commissions: COMMISSIONS,
  book_of_business: BOOK_OF_BUSINESS,
  ai_employees: AI_EMPLOYEES,
  sample_workflow: SAMPLE_WORKFLOW,
  scenarios: SCENARIOS,
  activity_feed_pool: ACTIVITY_FEED_POOL,
  health_clients:   HEALTH_CLIENTS,
  life_clients:     LIFE_CLIENTS,
  health_policies:  HEALTH_POLICIES,
  referral_partners: REFERRAL_PARTNERS,
  // Life Command Center / Book of Business / Messaging Command Center
  // demo-only sample book — see liveAdapters.js's demoClients() /
  // demoTransactions() / demoLifeThreads().
  life_book_transactions: LIFE_BOOK_TRANSACTIONS,
  life_book_threads:      LIFE_BOOK_THREADS,
};

// Real ESM default export. Previously this file did
// `module.exports = DEMO_SEED` (CJS) which webpack 5 stopped
// auto-synthesizing a `default` property for — production builds
// started failing with "does not contain a default export".
//
// The intermediate attempt — mark the CJS module as ESM via
// `Object.defineProperty(module.exports, '__esModule', ...)` — let
// the build compile but blew up at runtime: webpack 5's ESM strict
// mode sees any assignment to `module.exports` and throws
// "ES Modules may not assign module.exports or exports.*". The
// entire app fell over with a white page.
//
// Converting the file to a real ES module (this line) fixes both:
// the build finds the default, and the runtime never touches
// `module.exports`. The `import DEMO_SEED from '...'` pattern the
// three consumers use resolves cleanly. No consumer uses
// `require('./seed')`, so this is a zero-risk switch.
export default DEMO_SEED;
